import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import PageWrapper from '../../components/MainPageWrapper/PageWrapper';
import { APP_URL, ENDPOINT } from '../../constants/common-app.const';
import {
  ClientType,
  ListenerFullRow,
  PaymentMethod,
} from './CourseEnrollment.const';
import {
  ClientForm,
  INITIAL_CLIENT_FORM,
} from './CourseEnrollment.types';
import useGetCourseGroups from '../../hooks/useGetCourseGroups';
import useGetCourses from '../../hooks/useGetCourses';
import useCreateContract from '../../hooks/useCreateContract';
import useSubmitContract from '../../hooks/useSubmitContract';
import { useAuth } from '../../auth/AuthContext';
import api from '../../utils/api.util';
import toast from '../../utils/toast.util';
import Stepper from './components/Stepper';
import StepFooter from './components/StepFooter';
import ClientDataStep from './components/ClientDataStep';
import ListenersStep from './components/ListenersStep';
import ListenerModal from './components/ListenerModal';
import PreviousListenersModal, { PreviousListener } from './components/PreviousListenersModal';
import GroupAndPaymentStep from './components/GroupAndPaymentStep';
import DoneStep from './components/DoneStep';

import { isValidPassportDate, isValidEmail } from './CourseEnrollment.utils';
import css from './CourseEnrollment.module.css';

const emptyListener = (): ListenerFullRow => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  fullNameRu: '',
  fullNameBy: '',
  address: '',
  passport: '',
  passportIssueDate: '',
  passportIssuer: '',
  externalEmail: '',
  status: 'not_started',
});

const CourseEnrollment = () => {
  const { isAuthenticated } = useAuth();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const location = useLocation<{ title?: string; priceKopecks?: number; format?: string; duration?: string; existingContractId?: string; allowedClientTypes?: ClientType[] } | null>();
  const courseTitle = location.state?.title ?? 'Загрузка...';
  const priceKopecks = location.state?.priceKopecks ?? 0;
  const courseFormat = location.state?.format;
  const courseDuration = location.state?.duration;
  const existingContractId = location.state?.existingContractId ?? null;

  // Derive the course's allowed client types authoritatively from the course
  // itself, not just navigation state (which is lost on refresh / direct entry).
  // Defaulting to all three let users pick a disallowed type and only fail at
  // contract creation with CONTRACT_CLIENT_TYPE_NOT_ALLOWED. While the course
  // loads we expose none, so a disallowed type can never be selected.
  const { courses } = useGetCourses();
  const courseAllowedClientTypes = courses.find((c) => c.id === id)?.allowedClientTypes;
  const allowedClientTypes: ClientType[] = useMemo(
    () => location.state?.allowedClientTypes ?? courseAllowedClientTypes ?? [],
    [location.state, courseAllowedClientTypes],
  );

  const [step, setStep] = useState(1);
  const [clientType, setClientType] = useState<ClientType>(
    allowedClientTypes[0] ?? 'legal',
  );

  // Keep the selected client type within the allowed set once it resolves.
  useEffect(() => {
    if (allowedClientTypes.length && !allowedClientTypes.includes(clientType)) {
      setClientType(allowedClientTypes[0]);
    }
  }, [allowedClientTypes, clientType]);
  const [form, setForm] = useState<ClientForm>(INITIAL_CLIENT_FORM);
  const [prevRequisitesId, setPrevRequisitesId] = useState<string | undefined>(
    undefined,
  );
  const [listeners, setListeners] = useState<ListenerFullRow[]>([]);
  const [groupId, setGroupId] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('erip');
  const [consent, setConsent] = useState(false);
  const [contractId, setContractId] = useState<string | null>(null);
  const [contractUuid, setContractUuid] = useState<string | null>(null);
  const [contractNumber, setContractNumber] = useState<string | null>(null);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const [imnsVkods, setImnsVkods] = useState<string | null>(null);

  const [listenerModalOpen, setListenerModalOpen] = useState(false);
  const [editingListener, setEditingListener] =
    useState<ListenerFullRow | null>(null);
  const [prevListenersOpen, setPrevListenersOpen] = useState(false);
  const [prevListeners, setPrevListeners] = useState<PreviousListener[]>([]);
  const [prevListenersLoading, setPrevListenersLoading] = useState(false);

  const [prevRequisitesOptions, setPrevRequisitesOptions] = useState<{ value: string; label: string; clientData: any }[]>([]);
  const [eripData, setEripData] = useState<{ accountNumber: string; invoiceNumber: number; amountToPay: number; eripQRCodeLink: string } | null>(null);
  const [stepLoading, setStepLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [imnsLoading, setImnsLoading] = useState(false);
  const [listenerSaving, setListenerSaving] = useState(false);

  const [promoCode, setPromoCode] = useState<string>('');
  const [discountKopecks, setDiscountKopecks] = useState<number | null>(null);
  const [promoValidState, setPromoValidState] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [appliedPromoCode, setAppliedPromoCode] = useState<string | null>(null);
  const promoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const PAYMENT_METHOD_MAP: Record<PaymentMethod, string> = {
    card: 'alfabank',
    erip: 'erip',
    requisites: 'manual',
  };

  const { groups } = useGetCourseGroups({ courseId: id });
  const { createContract } = useCreateContract();
  const { submitContract } = useSubmitContract();

  // Auto-select first group when groups load
  useEffect(() => {
    if (groups.length > 0 && !groupId) {
      setGroupId(groups[0].id);
    }
  }, [groups, groupId]);

  // Sync paymentMethod with clientType
  useEffect(() => {
    if (clientType === 'individual') {
      setPaymentMethod((m) => (m === 'requisites' ? 'erip' : m));
    } else {
      setPaymentMethod('requisites');
    }
  }, [clientType]);

  // Pre-fill phone/email from user profile for fresh enrollments
  useEffect(() => {
    if (!isAuthenticated || existingContractId) return;
    api.get(ENDPOINT.PROFILE)
      .then((profile: any) => {
        setForm((prev) => ({
          ...prev,
          email: prev.email || profile.email || '',
          phone: prev.phone || profile.phoneNumber || '',
        }));
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Load previous requisites options from past contracts
  useEffect(() => {
    if (!isAuthenticated || existingContractId) return;
    api.getJson(ENDPOINT.CONTRACTS_MINE)
      .then((items: any[]) => {
        const CLIENT_TYPE_MAP: Record<string, ClientType> = {
          legal_entity: 'legal',
          sole_proprietor: 'ip',
          individual: 'individual',
        };
        const options = (Array.isArray(items) ? items : [])
          .filter((c) => c.isOwner && c.clientData)
          .map((c) => {
            const cd = c.clientData;
            const name = cd.vnaimp || cd.fioRu || 'Клиент';
            const label = cd.unp ? `${name} (УНП ${cd.unp})` : name;
            return { value: c.id, label, clientData: { ...cd, _clientType: CLIENT_TYPE_MAP[cd.clientType] ?? 'legal' } };
          });
        setPrevRequisitesOptions(options);
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Cleanup promo debounce timer on unmount
  useEffect(() => {
    return () => { if (promoTimerRef.current) clearTimeout(promoTimerRef.current); };
  }, []);

  const isoToDisplayDate = (iso: string | null) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return d && m && y ? `${d}.${m}.${y}` : '';
  };

  const handlePrevRequisitesChange = (id: string | undefined) => {
    setPrevRequisitesId(id);
    if (!id) return;
    const opt = prevRequisitesOptions.find((o) => o.value === id);
    if (!opt) return;
    const cd = opt.clientData;
    setClientType(cd._clientType);
    setForm({
      unp:               cd.unp || '',
      organization:      cd.vnaimp || '',
      legalAddress:      cd.legalAddress || '',
      postalDiffers:     cd.postalDiffers || false,
      postalAddress:     cd.postalAddress || '',
      phone:             cd.contactPhone || '',
      email:             cd.contactEmail || '',
      signerName:        cd.signatoryFio || '',
      signerPosition:    cd.signatoryPosition || '',
      signingBasis:      cd.signatoryBasis || '',
      funding:           cd.financingSource || 'own',
      passportNumber:    cd.passportNumber || '',
      passportIssueDate: isoToDisplayDate(cd.passportIssueDate),
      passportIssuer:    cd.passportIssuedBy || '',
      individualFullName: cd.fioRu || '',
    });
  };

  // Restore existing draft contract coming from "Мои курсы"
  useEffect(() => {
    if (!existingContractId) return;
    setContractId(existingContractId);
    setStep(2);

    api.getJson(`${ENDPOINT.CONTRACTS}/${existingContractId}`)
      .then((contract: any) => {
        const isoToDisplay = (iso: string | null) =>
          iso ? iso.split('-').reverse().join('.') : '';
        const restored: ListenerFullRow[] = (contract.listeners ?? [])
          .filter((l: any) => !l.deletedAt)
          .map((l: any) => ({
            id: l.listenerUid ?? l.id ?? `${Date.now()}-${Math.random()}`,
            fullNameRu: l.fioRu ?? '',
            fullNameBy: l.fioBy ?? '',
            address: l.address ?? '',
            passport: l.passportNumber ?? '',
            passportIssueDate: isoToDisplay(l.passportIssueDate),
            passportIssuer: l.passportIssuedBy ?? '',
            externalEmail: l.email ?? '',
            status: 'not_started' as const,
          }));
        setListeners(restored);
        if (contract.groupId) setGroupId(contract.groupId);
        if (contract.consentGiven) setConsent(true);
        if (contract.uuid) setContractUuid(contract.uuid);
      })
      .catch(() => toast.showOnFailure('Не удалось загрузить черновик договора'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingContractId]);

  const updateForm = <K extends keyof ClientForm>(key: K, val: ClientForm[K]) => {
    if (key === 'unp') setImnsVkods(null);
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const updateEditing = <K extends keyof ListenerFullRow>(
    key: K,
    val: ListenerFullRow[K],
  ) =>
    setEditingListener((prev) => (prev ? { ...prev, [key]: val } : prev));

  const openAddListener = () => {
    if (!contractId) {
      toast.showOnFailure('Сначала создайте договор на шаге 1');
      return;
    }
    setEditingListener(emptyListener());
    setListenerModalOpen(true);
  };

  const openAddFromContract = () => {
    if (!contractId) {
      toast.showOnFailure('Сначала создайте договор на шаге 1');
      return;
    }
    setEditingListener({
      ...emptyListener(),
      fullNameRu: form.signerName || '',
      externalEmail: form.email || '',
    });
    setListenerModalOpen(true);
  };

  const openAddSelf = async () => {
    if (!contractId) {
      toast.showOnFailure('Сначала создайте договор на шаге 1');
      return;
    }
    let prefill: Partial<ListenerFullRow> = {};
    try {
      const profile: any = await api.get(ENDPOINT.PROFILE);
      const nameParts = [profile.firstName, profile.lastName].filter(Boolean);
      prefill = {
        externalEmail: profile.email ?? '',
        fullNameRu: nameParts.join(' '),
      };
    } catch {
      // profile unavailable — open empty modal
    }
    setEditingListener({ ...emptyListener(), ...prefill });
    setListenerModalOpen(true);
  };

  const openEditListener = (l: ListenerFullRow) => {
    setEditingListener({ ...l });
    setListenerModalOpen(true);
  };

  const closeListenerModal = () => {
    setListenerModalOpen(false);
    setEditingListener(null);
  };

  const openAddFromPrevious = async () => {
    if (!contractId) {
      toast.showOnFailure('Сначала создайте договор на шаге 1');
      return;
    }
    setPrevListenersOpen(true);
    setPrevListenersLoading(true);
    try {
      const rows = await api.getJson(ENDPOINT.LISTENERS_PREVIOUS);
      setPrevListeners(Array.isArray(rows) ? rows : []);
    } catch {
      toast.showOnFailure('Ошибка загрузки слушателей');
      setPrevListenersOpen(false);
    } finally {
      setPrevListenersLoading(false);
    }
  };

  const selectPreviousListener = (prev: PreviousListener) => {
    const isoToDisplay = (iso: string | null) =>
      iso ? iso.split('-').reverse().join('.') : '';
    setEditingListener({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      fullNameRu: prev.fioRu ?? '',
      fullNameBy: prev.fioBy ?? '',
      address: prev.address ?? '',
      passport: prev.passportNumber ?? '',
      passportIssueDate: isoToDisplay(prev.passportIssueDate),
      passportIssuer: prev.passportIssuedBy ?? '',
      externalEmail: prev.email ?? '',
      status: 'not_started',
    });
    setPrevListenersOpen(false);
    setListenerModalOpen(true);
  };

  const saveListener = async () => {
    if (!editingListener) return;

    if (!editingListener.fullNameRu?.trim()) {
      toast.showOnFailure('ФИО на русском обязательно');
      return;
    }
    if (!editingListener.fullNameBy?.trim()) {
      toast.showOnFailure('ФИО на белорусском обязательно');
      return;
    }
    if (!editingListener.passport?.trim()) {
      toast.showOnFailure('Серия и номер паспорта обязательны');
      return;
    }
    if (!editingListener.passportIssueDate?.trim()) {
      toast.showOnFailure('Дата выдачи паспорта обязательна');
      return;
    }
    if (!isValidPassportDate(editingListener.passportIssueDate)) {
      toast.showOnFailure('Дата выдачи паспорта должна быть в формате ДД.ММ.ГГГГ (например, 01.02.2013)');
      return;
    }
    if (!editingListener.passportIssuer?.trim()) {
      toast.showOnFailure('Кем выдан паспорт — обязательное поле');
      return;
    }
    if (!editingListener.externalEmail?.trim()) {
      toast.showOnFailure('E-mail для доступа обязателен');
      return;
    }
    if (!isValidEmail(editingListener.externalEmail)) {
      toast.showOnFailure('Введите корректный e-mail адрес');
      return;
    }

    const isEdit = listeners.some((l) => l.id === editingListener.id);

    const passportIssueDateIso = editingListener.passportIssueDate
      ? editingListener.passportIssueDate.split('.').reverse().join('-')
      : null;
    const apiBody = {
      fioRu: editingListener.fullNameRu,
      fioBy: editingListener.fullNameBy || null,
      address: editingListener.address || null,
      passportNumber: editingListener.passport || null,
      passportIssueDate: passportIssueDateIso,
      passportIssuedBy: editingListener.passportIssuer || null,
      email: editingListener.externalEmail || null,
    };

    setListenerSaving(true);
    try {
      if (isEdit) {
        if (contractId) {
          try {
            await api.patch(
              `${ENDPOINT.CONTRACTS}/${contractId}/listeners/${editingListener.id}`,
              apiBody
            );
          } catch {
            toast.showOnFailure('Ошибка обновления слушателя');
            return;
          }
        }
        setListeners((prev) =>
          prev.map((l) => (l.id === editingListener.id ? editingListener : l))
        );
      } else {
        if (contractId) {
          try {
            const response = await api.postJson(
              `${ENDPOINT.CONTRACTS}/${contractId}/listeners`,
              apiBody
            );
            const newListener: ListenerFullRow = {
              ...editingListener,
              id: response.listenerUid,
            };
            setListeners((prev) => [...prev, newListener]);
          } catch {
            toast.showOnFailure('Ошибка добавления слушателя');
            return;
          }
        } else {
          setListeners((prev) => [...prev, editingListener]);
        }
      }
      closeListenerModal();
    } finally {
      setListenerSaving(false);
    }
  };

  const removeListener = async (lid: string) => {
    if (contractId) {
      try {
        await api.delete(`${ENDPOINT.CONTRACTS}/${contractId}/listeners/${lid}`);
      } catch {
        toast.showOnFailure('Ошибка удаления слушателя');
        return;
      }
    }
    setListeners((prev) => prev.filter((l) => l.id !== lid));
  };

  const isEditMode =
    !!editingListener && listeners.some((l) => l.id === editingListener.id);

  const baseSumKopecks = priceKopecks * listeners.length;
  const effectiveSumKopecks =
    promoValidState === 'valid' && discountKopecks !== null
      ? baseSumKopecks - discountKopecks * listeners.length
      : baseSumKopecks;
  const totalPrice = useMemo(
    () => `${(effectiveSumKopecks / 100).toFixed(2).replace('.', ',')} BYN`,
    [effectiveSumKopecks]
  );

  const selectedGroup = groups.find((g) => g.id === groupId);

  const validateStep1 = (): string | null => {
    if (clientType === 'legal') {
      if (!form.unp.trim()) return 'Введите УНП';
      if (!form.organization.trim()) return 'Введите наименование организации';
      if (!form.legalAddress.trim()) return 'Введите юридический адрес';
      if (!form.signerName.trim()) return 'Введите ФИО подписанта';
      if (!form.signerPosition.trim()) return 'Введите должность подписанта';
      if (!form.signingBasis.trim()) return 'Введите основание подписания';
    }
    if (clientType === 'ip') {
      if (!form.unp.trim()) return 'Введите УНП';
      if (!form.organization.trim()) return 'Введите наименование ИП';
      if (!form.postalAddress.trim()) return 'Введите почтовый адрес';
    }
    if (clientType === 'individual') {
      if (!form.individualFullName.trim()) return 'Введите ФИО';
      if (!form.passportNumber.trim()) return 'Введите номер паспорта';
      if (!form.passportIssueDate.trim()) return 'Введите дату выдачи паспорта';
      if (!isValidPassportDate(form.passportIssueDate)) return 'Дата выдачи паспорта должна быть в формате ДД.ММ.ГГГГ (например, 01.02.2013)';
      if (!form.passportIssuer.trim()) return 'Введите орган выдачи паспорта';
      if (!form.postalAddress.trim()) return 'Введите почтовый адрес';
    }
    if (!form.phone.trim()) return 'Введите телефон';
    if (!form.email.trim()) return 'Введите e-mail';
    if (!isValidEmail(form.email)) return 'Введите корректный e-mail адрес';
    return null;
  };

  const handleImnsLookup = async () => {
    if (!form.unp.trim()) {
      toast.showOnFailure('Введите УНП перед запросом к ИМНС');
      return;
    }
    setImnsLoading(true);
    try {
      const result = await api.getJson(`${ENDPOINT.CLIENTS_IMNS}/${form.unp.trim()}`);
      if (result.manualEntry) {
        toast.showOnFailure('API ИМНС временно недоступен — введите данные вручную');
        return;
      }
      if (result.vnaimp) updateForm('organization', result.vnaimp);
      if (result.vpadres) updateForm('legalAddress', result.vpadres);
      setImnsVkods(result.vkods ?? null);
    } catch (err: any) {
      if (err?.errorCode === 'IMNS_INACTIVE_UNP') {
        toast.showOnFailure('УНП недействителен — организация не является действующей');
      } else {
        toast.showOnFailure('Ошибка запроса к ИМНС — введите данные вручную');
      }
    } finally {
      setImnsLoading(false);
    }
  };

  const handleViewContract = async () => {
    if (!contractUuid) return;
    try {
      const html = await api.getText(`${ENDPOINT.CONTRACTS}/${contractUuid}`);
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (w) setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch {
      toast.showOnFailure('Не удалось загрузить договор');
    }
  };

  const handlePromoCodeChange = (value: string) => {
    setPromoCode(value);
    if (promoTimerRef.current) clearTimeout(promoTimerRef.current);
    const trimmed = value.trim().toUpperCase();
    if (!trimmed) {
      setPromoValidState('idle');
      setDiscountKopecks(null);
      setAppliedPromoCode(null);
      return;
    }
    setPromoValidState('validating');
    promoTimerRef.current = setTimeout(async () => {
      try {
        const result = await api.getJson(
          `${ENDPOINT.COURSES}/${id}/promo-codes/${encodeURIComponent(trimmed)}/validate`
        );
        if (result.valid) {
          setPromoValidState('valid');
          setDiscountKopecks(result.discountKopecks);
          setAppliedPromoCode(trimmed);
        } else {
          setPromoValidState('invalid');
          setDiscountKopecks(null);
          setAppliedPromoCode(null);
        }
      } catch {
        toast.showOnFailure('Не удалось проверить промокод');
        setPromoValidState('invalid');
        setDiscountKopecks(null);
        setAppliedPromoCode(null);
      }
    }, 500);
  };

  const goNext = async () => {
    if (step === 1) {
      if (contractId) {
        setStep(2);
        return;
      }
      const validationError = validateStep1();
      if (validationError) {
        toast.showOnFailure(validationError);
        return;
      }
      try {
        setStepLoading(true);
        const result = await createContract({
          courseId: id,
          groupId: groupId || null,
          consentGiven: consent,
          clientType,
          manuallyEntered: clientType === 'individual' || !imnsVkods,
          vkods: imnsVkods,
          form,
        });
        setContractId(result.contractId);
        setContractUuid(result.contractUuid);
        setStep(2);
      } catch {
        // toast already shown by useCreateContract
      } finally {
        setStepLoading(false);
      }
      return;
    }
    if (step === 2 && listeners.length === 0) {
      toast.showOnFailure('Добавьте хотя бы одного слушателя');
      return;
    }
    if (step < 4) setStep(step + 1);
    else history.push(APP_URL.MY_COURSES);
  };

  const goBack = () => {
    if (step === 1) history.push(APP_URL.COURSES);
    else {
      if (step === 3 && promoValidState !== 'idle') {
        setPromoCode('');
        setPromoValidState('idle');
        setDiscountKopecks(null);
        setAppliedPromoCode(null);
      }
      setStep(step - 1);
    }
  };

  const handlePay = async () => {
    if (!contractId) return;
    setPayLoading(true);
    try {
      await api.patch(`${ENDPOINT.CONTRACTS}/${contractId}`, {
        consentGiven: true,
        groupId: groupId || null,
        ...(promoValidState === 'valid' && appliedPromoCode ? { promoCode: appliedPromoCode } : {}),
      });
      const submitResult = await submitContract(contractId);
      setContractNumber(submitResult.contractNumber);

      let paymentResult;
      try {
        const backendMethod = PAYMENT_METHOD_MAP[paymentMethod];
        paymentResult = await api.postJson(
          `${ENDPOINT.CONTRACTS}/${contractId}/payments`,
          { method: backendMethod }
        );
      } catch {
        toast.showOnFailure('Ошибка инициализации оплаты');
        return;
      }

      if (paymentMethod === 'card') {
        const formUrl = paymentResult?.result?.formUrl;
        if (formUrl) {
          window.location.href = formUrl;
          return;
        }
        toast.showOnFailure('Не удалось получить ссылку на оплату');
        return;
      }

      if (paymentMethod === 'erip') {
        const r = paymentResult?.result;
        setEripData({
          accountNumber: r?.eripAccountNumber ?? '',
          invoiceNumber: r?.eripInvoiceNumber ?? 0,
          amountToPay: r?.amountToPay ?? 0,
          eripQRCodeLink: r?.eripQRCodeLink ?? '',
        });
      }

      if (paymentMethod === 'requisites') {
        setInvoiceUrl(paymentResult?.result?.invoiceUrl ?? null);
      }

      setStep(4);
    } catch {
      // toast already shown by useSubmitContract
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <PageWrapper>
      <div className={css.Wrapper}>
        <h1 className={css.Title}>Оформление договора</h1>
        <p className={css.Subtitle}>Курс: {courseTitle}</p>

        <Stepper step={step} />

        {step === 1 && (
          <ClientDataStep
            clientType={clientType}
            onClientTypeChange={setClientType}
            allowedClientTypes={allowedClientTypes}
            form={form}
            updateForm={updateForm}
            prevRequisitesId={prevRequisitesId}
            prevRequisitesOptions={prevRequisitesOptions}
            onPrevRequisitesChange={handlePrevRequisitesChange}
            onImnsLookup={handleImnsLookup}
            imnsLoading={imnsLoading}
          />
        )}

        {step === 2 && (
          <ListenersStep
            courseTitle={courseTitle}
            courseFormat={courseFormat}
            courseDuration={courseDuration}
            clientType={clientType}
            form={form}
            listeners={listeners}
            onAdd={openAddListener}
            onAddSelf={openAddSelf}
            onAddFromContract={openAddFromContract}
            onAddFromPrevious={openAddFromPrevious}
            onEdit={openEditListener}
            onRemove={removeListener}
          />
        )}

        {step === 3 && (
          <GroupAndPaymentStep
            groups={groups}
            courseTitle={courseTitle}
            contractNumber={contractNumber ?? ''}
            listenersCount={listeners.length}
            pricePerStudent={`${Math.round(priceKopecks / 100)} BYN / слушатель`}
            totalPrice={totalPrice}
            groupId={groupId}
            onGroupChange={setGroupId}
            consent={consent}
            onConsentChange={setConsent}
            clientType={clientType}
            paymentMethod={paymentMethod}
            onPaymentMethodChange={setPaymentMethod}
            onPay={handlePay}
            payLoading={payLoading}
            onViewContract={handleViewContract}
            promoCode={promoCode}
            onPromoCodeChange={handlePromoCodeChange}
            promoValidState={promoValidState}
            discountKopecks={discountKopecks !== null ? discountKopecks * listeners.length : null}
            appliedPromoCode={appliedPromoCode}
            basePriceFormatted={`${(baseSumKopecks / 100).toFixed(2).replace('.', ',')} BYN`}
          />
        )}

        {step === 4 && (
          <DoneStep
            contractNumber={contractNumber ?? '—'}
            group={selectedGroup}
            email={form.email}
            paymentMethod={paymentMethod}
            eripData={eripData}
            invoiceUrl={invoiceUrl}
            onMyCourses={() => history.push(APP_URL.MY_COURSES)}
            onCoursesList={() => history.push(APP_URL.COURSES)}
          />
        )}

        {step !== 3 && step !== 4 && (
          <StepFooter onBack={goBack} onNext={goNext} loading={stepLoading} />
        )}

        {step === 3 && <StepFooter onBack={goBack} hideNext borderless />}
      </div>

      <ListenerModal
        open={listenerModalOpen}
        isEditMode={isEditMode}
        listener={editingListener}
        onChange={updateEditing}
        onClose={closeListenerModal}
        onSave={saveListener}
        saving={listenerSaving}
      />

      <PreviousListenersModal
        open={prevListenersOpen}
        loading={prevListenersLoading}
        listeners={prevListeners}
        onSelect={selectPreviousListener}
        onClose={() => setPrevListenersOpen(false)}
      />
    </PageWrapper>
  );
};

export default CourseEnrollment;
